import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, CheckSquare, Bell, Calendar, Clock, MessageSquare, Settings, LogOut, Search, X, Users, Activity } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../i18n';
import { trackNavigation, trackAction } from '../utils/analytics';
import apiClient from '../utils/api';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t, currentLang, setLanguage } = useTranslation();
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (searchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    
    // Lock body scroll on mobile when search is expanded
    if (window.innerWidth <= 768) {
      if (searchExpanded) {
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
      }
    }
    
    return () => {
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [searchExpanded]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowResults(false);
        if (searchExpanded && !searchQuery) {
          setSearchExpanded(false);
        }
      }
    };
    
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && searchExpanded) {
        handleSearchClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [searchExpanded, searchQuery]);

  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    
    setSearching(true);
    try {
      const response = await apiClient.get(`/search?q=${encodeURIComponent(query)}&limit=20`);
      setSearchResults(response.data || []);
      setShowResults(true);
      trackAction('search', 'navigation', query);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search - wait 300ms after user stops typing
    if (query.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(query);
      }, 300);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  const handleSearchToggle = () => {
    setSearchExpanded(!searchExpanded);
    if (!searchExpanded) {
      setSearchQuery('');
      setSearchResults([]);
      setShowResults(false);
    }
  };

  const handleSearchClose = () => {
    setSearchExpanded(false);
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      performSearch(searchQuery);
    }
  };

  const handleResultClick = (result) => {
    const { entity_type, entity_id } = result.metadata || {};
    setShowResults(false);
    setSearchExpanded(false);
    setSearchQuery('');
    
    if (entity_type === 'task') navigate('/tasks');
    else if (entity_type === 'reminder') navigate('/reminders');
    else if (entity_type === 'calendar') navigate('/calendar');
    else if (entity_type === 'conversation') navigate('/conversations');
    else if (entity_type === 'memory') navigate('/chat');
  };

  const isAdminRoute = location.pathname.startsWith('/admin');
  
  const navItems = isAdminRoute ? [
    { path: '/admin', icon: Home, label: 'Dashboard', key: 'admin-dashboard' },
    { path: '/admin', icon: Users, label: 'Users', key: 'admin-users' },
    { path: '/admin', icon: Activity, label: 'Analytics', key: 'admin-analytics' },
    { path: '/admin', icon: Settings, label: 'Settings', key: 'admin-settings' }
  ] : [
    { path: '/', icon: Home, label: t('dashboard') },
    { path: '/tasks', icon: CheckSquare, label: t('tasks') },
    { path: '/reminders', icon: Bell, label: t('reminders') },
    { path: '/calendar', icon: Calendar, label: t('calendar') },
    { path: '/chat', icon: MessageSquare, label: t('chat') },
    { path: '/conversations', icon: Clock, label: t('history') },
    { path: '/config', icon: Settings, label: t('config') }
  ];

  return (
    <>
      {/* Top navigation for larger screens */}
      <nav className="nav">
        <div className="nav-content">
          <Link to={isAdminRoute ? "/admin" : "/"} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none' }}>
            <div style={{
              width: '2.5rem',
              height: '2.5rem',
              background: isAdminRoute ? 'linear-gradient(135deg, #dc2626 0%, #ea580c 100%)' : 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              borderRadius: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className={isAdminRoute ? "fas fa-shield-alt" : "fas fa-brain"} style={{ color: 'white', fontSize: '1.125rem' }}></i>
            </div>
            <h1 className="gradient-text" style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>
              {isAdminRoute ? 'SmartGTD Admin' : 'SmartGTD'}
            </h1>
          </Link>
          <div className="nav-menu" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, justifyContent: 'flex-end' }}>
            {/* Search Box */}
            <div ref={searchContainerRef} style={{ 
              display: 'flex', 
              alignItems: 'center',
              position: 'relative',
              transition: 'all 0.3s ease',
              flex: searchExpanded ? 1 : 'none',
              maxWidth: searchExpanded ? '500px' : 'auto',
              width: searchExpanded ? '100%' : 'auto'
            }}>
              <form onSubmit={handleSearchSubmit} style={{ display: 'flex', alignItems: 'center', width: '100%', position: 'relative' }}>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={t('searchTasks') || 'Search...'}
                  style={{
                    flex: searchExpanded ? 1 : 0,
                    width: searchExpanded ? 'auto' : '0',
                    padding: searchExpanded ? '0.75rem 2.5rem 0.75rem 1rem' : '0',
                    border: searchExpanded ? '2px solid #e5e7eb' : 'none',
                    borderRadius: '0.75rem',
                    fontSize: '0.875rem',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: searchExpanded ? 1 : 0,
                    outline: 'none',
                    boxShadow: searchExpanded ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                  }}
                  onFocus={() => searchQuery && setShowResults(true)}
                />
                {searching && searchExpanded && (
                  <div style={{
                    position: 'absolute',
                    right: '0',
                    top: '0',
                    bottom: '0',
                    padding: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none'
                  }}>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid #e5e7eb',
                      borderTopColor: '#2563eb',
                      borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite'
                    }} />
                  </div>
                )}
                <button
                  type="button"
                  onClick={searchExpanded ? handleSearchClose : handleSearchToggle}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7280',
                    transition: 'color 0.2s',
                    position: searchExpanded ? 'absolute' : 'relative',
                    right: searchExpanded ? '0.5rem' : 'auto',
                    opacity: searching ? 0 : 1,
                    pointerEvents: searching ? 'none' : 'auto'
                  }}
                >
                  {searchExpanded ? <X size={18} /> : <Search size={20} />}
                </button>
              </form>
              
              {/* Search Results Dropdown */}
              {showResults && searchResults.length > 0 && (
                <div style={{
                  position: window.innerWidth <= 768 ? 'fixed' : 'absolute',
                  top: window.innerWidth <= 768 ? '60px' : 'calc(100% + 0.5rem)',
                  left: window.innerWidth <= 768 ? 0 : 0,
                  right: 0,
                  bottom: window.innerWidth <= 768 ? 0 : 'auto',
                  backgroundColor: 'white',
                  border: window.innerWidth <= 768 ? 'none' : '1px solid #e5e7eb',
                  borderRadius: window.innerWidth <= 768 ? 0 : '0.75rem',
                  boxShadow: window.innerWidth <= 768 ? 'none' : '0 10px 25px rgba(0,0,0,0.15), 0 4px 6px rgba(0,0,0,0.1)',
                  maxHeight: window.innerWidth <= 768 ? 'none' : '400px',
                  height: window.innerWidth <= 768 ? 'auto' : 'auto',
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  zIndex: 1000,
                  minWidth: window.innerWidth <= 768 ? 'auto' : '320px'
                }}>
                  <div style={{
                    padding: '0.75rem 1rem',
                    borderBottom: '1px solid #f3f4f6',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    zIndex: 1
                  }}>
                    {searchResults.length} {searchResults.length === 1 ? t('searchResult') : t('searchResults')}
                  </div>
                  {searchResults.map((result, idx) => {
                    const entityType = result.metadata?.entity_type || 'unknown';
                    const entityConfig = {
                      task: { bg: '#eff6ff', color: '#2563eb', icon: '✓', label: 'Task' },
                      reminder: { bg: '#fef3c7', color: '#d97706', icon: '🔔', label: 'Reminder' },
                      calendar: { bg: '#f0fdf4', color: '#16a34a', icon: '📅', label: 'Event' },
                      conversation: { bg: '#f3f4f6', color: '#6b7280', icon: '💬', label: 'Chat' },
                      memory: { bg: '#faf5ff', color: '#9333ea', icon: '🧠', label: 'Memory' }
                    };
                    const config = entityConfig[entityType] || entityConfig.conversation;
                    
                    // For memory entities, use text if title is missing
                    let title = result.metadata?.title;
                    if (!title && entityType === 'memory' && result.metadata?.text) {
                      title = result.metadata.text.substring(0, 60) + (result.metadata.text.length > 60 ? '...' : '');
                    }
                    if (!title) title = 'Untitled';

                    // Prefer the gated relevance over raw cosine similarity: a
                    // correct result often has a low cosine score, and showing
                    // "11% match" next to the right answer reads as broken.
                    const confidence = typeof result.relevance === 'number' ? result.relevance : result.score;
                    const showConfidence = typeof confidence === 'number' && confidence > 0;
                    
                    // Check if synced with Google
                    const isGoogleSynced = result.metadata?.integration_name === 'google_tasks' || 
                                          result.metadata?.integration_name === 'google_calendar';
                    
                    return (
                      <div
                        key={idx}
                        onClick={() => handleResultClick(result)}
                        style={{
                          padding: '0.875rem 1rem',
                          borderBottom: idx < searchResults.length - 1 ? '1px solid #f3f4f6' : 'none',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease',
                          backgroundColor: 'white'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{
                            width: '2rem',
                            height: '2rem',
                            borderRadius: '0.5rem',
                            backgroundColor: config.bg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                            flexShrink: 0
                          }}>
                            {config.icon}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ 
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              color: '#111827',
                              marginBottom: '0.25rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {title}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{
                                fontSize: '0.7rem',
                                padding: '0.125rem 0.375rem',
                                borderRadius: '0.25rem',
                                backgroundColor: config.bg,
                                color: config.color,
                                fontWeight: '600',
                                textTransform: 'uppercase'
                              }}>
                                {config.label}
                              </span>
                              {isGoogleSynced && (
                                <span style={{
                                  fontSize: '0.7rem',
                                  padding: '0.125rem 0.375rem',
                                  borderRadius: '0.25rem',
                                  backgroundColor: '#e8f5e9',
                                  color: '#2e7d32',
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }} title="Synced with Google">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                  </svg>
                                  Google
                                </span>
                              )}
                              {result.metadata?.entity_name && (
                                <span style={{
                                  fontSize: '0.7rem',
                                  color: '#6b7280',
                                  fontWeight: '600'
                                }}>
                                  {result.metadata.entity_name}
                                </span>
                              )}
                              {showConfidence && (
                                <span style={{
                                  fontSize: '0.7rem',
                                  color: '#9ca3af',
                                  fontWeight: '500'
                                }}>
                                  {(confidence * 100).toFixed(0)}% {t('searchMatch')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Desktop: Show all nav items */}
            {!searchExpanded && (
              <div className="desktop-only" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '1rem'
              }}>
                {navItems.map(({ path, icon: Icon, label, key }) => (
                  <Link
                    key={key || path}
                    to={path}
                    onClick={() => trackNavigation(label)}
                    style={{
                      textDecoration: 'none',
                      color: location.pathname === path ? '#2563eb' : '#666',
                      fontWeight: location.pathname === path ? '600' : '400',
                      fontSize: '0.75rem',
                      padding: '0.5rem',
                      borderRadius: '0.5rem',
                      backgroundColor: location.pathname === path ? '#eff6ff' : 'transparent',
                      transition: 'all 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.25rem',
                      minWidth: '60px'
                    }}
                  >
                    <Icon size={18} />
                    <span>{label}</span>
                  </Link>
                ))}
              </div>
            )}
            
            {/* Language selector */}
            {!searchExpanded && (
              <select
                value={currentLang} 
                onChange={(e) => {
                  setLanguage(e.target.value);
                  trackAction('language_changed', 'settings', e.target.value);
                }}
                style={{
                  border: 'none',
                  borderRadius: '0.5rem',
                  padding: '0.25rem',
                  fontSize: '1.25rem',
                  backgroundColor: 'transparent',
                  cursor: 'pointer'
                }}
              >
                <option value="en">🇺🇸</option>
                <option value="pt">🇧🇷</option>
                <option value="es">🇪🇸</option>
              </select>
            )}
            
            {/* Logout button */}
            {!searchExpanded && (
              <button
                onClick={() => {
                  trackAction('logout', 'auth');
                  logout();
                }} 
                className="nav-button" 
                title={t('logout')}
              >
                <LogOut size={20} />
                <span className="sr-only">{t('logout')}</span>
              </button>
            )}
            </div>
        </div>
      </nav>

      {/* Bottom navigation for mobile */}
      <nav className="bottom-nav">
        {navItems.map(({ path, icon: Icon, label, key }) => (
          <Link
            key={key || path}
            to={path}
            onClick={() => trackNavigation(label)}
            className={`bottom-nav-item ${location.pathname === path ? 'active' : ''}`}
          >
            <Icon size={20} />
            <span>{label}</span>
          </Link>
        ))}
      </nav>
    </>
  );
};

export default Navigation;